import { useSearchParams } from 'react-router-dom'
import { useAuthStore } from '../store/auth'
import { useMyDashboard } from '../hooks/useMyDashboard'
import { useNotifications } from '../hooks/useNotifications'
import { useIsMobile } from '../hooks/useIsMobile'
import { PublicLayout } from '../components/layout/PublicLayout'
import { AccountHeader } from '../components/account/AccountHeader'
import { AccountTabs, type AccountTab } from '../components/account/AccountTabs'
import { OverviewTab } from '../components/account/tabs/OverviewTab'
import { MyEventsTab } from '../components/account/tabs/MyEventsTab'
import { FollowingTab } from '../components/account/tabs/FollowingTab'
import { NotificationsTab } from '../components/account/tabs/NotificationsTab'
import { SettingsTab } from '../components/account/tabs/SettingsTab'

const VALID_TABS: AccountTab[] = ['overview', 'events', 'following', 'notifications', 'settings']

function parseTab(raw: string | null): AccountTab {
  return raw && (VALID_TABS as string[]).includes(raw) ? (raw as AccountTab) : 'overview'
}

export default function AccountPage() {
  const { user } = useAuthStore()
  const isMobile = useIsMobile()
  const [searchParams, setSearchParams] = useSearchParams()
  const tab = parseTab(searchParams.get('tab'))

  const { data: dashData, isLoading: dashLoading } = useMyDashboard()
  const { data: notifData, isLoading: notifLoading } = useNotifications()

  const dashboard = dashData?.data
  const notifications = notifData?.data?.notifications ?? []
  const unreadCount = notifData?.data?.unreadCount ?? 0

  function setTab(next: AccountTab) {
    setSearchParams(next === 'overview' ? {} : { tab: next }, { replace: false })
  }

  return (
    <PublicLayout>
      <div style={{ maxWidth: 1080, margin: '0 auto', padding: isMobile ? '0 16px' : '0 40px', width: '100%' }}>
        {/* Identity strip */}
        <div style={{ padding: isMobile ? '24px 0 0' : '34px 0 0' }}>
          {user && <AccountHeader user={user} onEdit={() => setTab('settings')} />}
        </div>

        {/* Tabs */}
        <div style={{ marginTop: 22 }}>
          <AccountTabs active={tab} onChange={setTab} unreadCount={unreadCount} />
        </div>
        <div style={{ height: 1, background: 'var(--line-soft)', marginTop: 14 }} />

        {/* Body */}
        <div style={{ padding: isMobile ? '24px 0 60px' : '28px 0 72px' }}>
          {(tab === 'overview' || tab === 'events' || tab === 'following') && (
            <>
              {dashLoading && <p style={{ fontSize: 13, color: 'var(--muted)' }}>Loading…</p>}
              {!dashLoading && dashboard && (
                <>
                  {tab === 'overview' && <OverviewTab dashboard={dashboard} notifications={notifications} />}
                  {tab === 'events' && <MyEventsTab dashboard={dashboard} />}
                  {tab === 'following' && <FollowingTab dashboard={dashboard} />}
                </>
              )}
            </>
          )}

          {tab === 'notifications' && (
            <NotificationsTab notifications={notifications} unreadCount={unreadCount} isLoading={notifLoading} />
          )}

          {tab === 'settings' && <SettingsTab />}
        </div>
      </div>
    </PublicLayout>
  )
}
